import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { msalConfig } from './constants/authConfig';
import App from './app/App';
import reportWebVitals from './reportWebVitals';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { createRoot } from 'react-dom/client';
import { store, persistor } from './app/store';
import { PersistGate } from 'redux-persist/integration/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@mui/material';
import { theme } from 'styles/theme';
import { ErrorBoundary } from 'react-error-boundary';
import { AppErrorFallback } from 'components/Fallbacks/ErrorFallback';
import { FullScreenLoading } from 'components/loading';
import i18n, { i18nReady } from 'i18n/config';
import { reloadOnStaleChunk } from 'app/reloadOnStaleChunk';

reloadOnStaleChunk();

const container = document.getElementById('root')!;
const root = createRoot(container);
const queryClient = new QueryClient();

export const msalInstance = new PublicClientApplication(msalConfig);

// The MSAL redirect URI registered in Azure AD. The popup (and any redirect
// flow) lands here. This page must stay minimal: it only lets msal-browser
// process the auth response. It MUST NOT mount the router — React Router
// navigation strips the auth hash from the URL, and whether that happens before
// or after MSAL reads the hash is a race that Chrome wins and Edge loses, which
// is why SSO worked in Chrome but not Edge.
const isAuthCallback = window.location.pathname === '/auth-callback';

// The public download page (/download/{id}) is opened by external recipients of a shared file,
// who have no account. It renders on its own, without MSAL, the store or the router: the app's
// routes would send a signed-out visitor to /login.
const publicDownloadId = window.location.pathname.match(/^\/download\/([^/]+)\/?$/)?.[1];

async function bootstrapApp() {
  if (publicDownloadId) {
    const { DownloadPage, downloadPageLanguage } = await import('features/PublicDownload/DownloadPage');
    // The page has its own EN/DE choice, not the signed-in user's language; switching before the
    // first render keeps a German visitor from seeing a frame of English.
    await i18nReady;
    await i18n.changeLanguage(downloadPageLanguage());
    root.render(<DownloadPage fileId={publicDownloadId} />);
    return;
  }

  try {
    await msalInstance.initialize();

    const response = await msalInstance.handleRedirectPromise();

    if (response?.account) {
      msalInstance.setActiveAccount(response.account);
    } else if (!msalInstance.getActiveAccount()) {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
      }
    }
  } catch (error: any) {
    console.error("MSAL initialization or redirect error", error);
  }

  if (isAuthCallback) {
    // In the popup, the opener reads the result and closes this window, so this
    // navigation never runs. In a full-page redirect flow (no opener) we return
    // to the app root once the response has been processed above.
    if (!window.opener) {
      window.location.replace('/');
    }
    return;
  }

  // Opt-in local mocks for endpoints whose backend is not ready yet. Dynamically imported so msw is
  // only fetched when the flag is on, and hard-disabled in a production build.
  if (process.env.NODE_ENV !== 'production') {
    const { isAnyMockEnabled, startMocks } = await import('./mocks/browser');

    if (isAnyMockEnabled()) {
      await startMocks();
    }
  }

  // Dictionaries are fetched on demand now, so wait for the active one before the first paint —
  // otherwise a returning non-English user gets a frame of English. `en` is bundled, so this
  // resolves immediately for English and for a first-ever visit.
  await i18nReady;

  root.render(
    <Provider store={store}>
      <MsalProvider instance={msalInstance}>
        <BrowserRouter>
          <PersistGate loading={<FullScreenLoading />} persistor={persistor}>
            <QueryClientProvider client={queryClient}>
              <ThemeProvider theme={theme}>
                <ErrorBoundary FallbackComponent={AppErrorFallback} onReset={() => window.location.reload()}>
                  <App />
                </ErrorBoundary>
              </ThemeProvider>
            </QueryClientProvider>
          </PersistGate>
        </BrowserRouter>
      </MsalProvider>
    </Provider>
  );
}

bootstrapApp();

reportWebVitals();
